// src/receptionist/flowState.tsx
//
// Loads a merchant's conversation (opener, steps, follow-ups) and saves it back
// (ourteam-frontend#3608, BE ourteam-backend#4668). Same shape as
// merchantState.tsx: one fetch, the render states, and the save rule.
//
// The save rule here: a rejected flow comes back as `problems` in the
// validator's own words — the same twelve rules the FE runs locally, in the
// same strings — and the screen renders them in ONE list with its own. The
// merchant should not have to care which side found the problem.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { AlertTriangleIcon, Loader2Icon } from "lucide-react";
import { Card, CardBody, EmptyState, Button } from "./components/kit";
import { useSnackbar } from "@hook/use-snackbar";
import { getFlow, saveFlow, type BookingFlow } from "@service/receptionist";

export type FlowSaveOutcome =
    | { ok: true; flow: BookingFlow }
    | { ok: false; problems: string[] };

export type FlowScreenProps = {
    flow: BookingFlow;
    /** Resolves with the server's flow on success, or the server's problems.
     *  A transport failure is reported as a toast and resolves as an empty
     *  problem list, so the screen keeps its draft and its dirty flag. */
    onSave: (next: BookingFlow) => Promise<FlowSaveOutcome>;
};

type State =
    | { kind: "loading" }
    | { kind: "error" }
    | { kind: "ready"; flow: BookingFlow };

export function useFlow(merchantId: string | null) {
    const [state, setState] = useState<State>({ kind: "loading" });
    const seq = useRef(0);
    const snackbar = useSnackbar();

    const load = useCallback(async () => {
        const mine = ++seq.current;
        if (!merchantId) { setState({ kind: "error" }); return; }
        setState({ kind: "loading" });
        try {
            const flow = await getFlow(merchantId);
            if (seq.current !== mine) return;
            setState({ kind: "ready", flow });
        } catch {
            if (seq.current !== mine) return;
            setState({ kind: "error" });
        }
    }, [merchantId]);

    useEffect(() => { void load(); }, [load]);

    const save = useCallback(async (next: BookingFlow): Promise<FlowSaveOutcome> => {
        if (!merchantId) return { ok: false, problems: [] };
        const result = await saveFlow(merchantId, next);
        if (result.ok) {
            setState({ kind: "ready", flow: result.flow });
            snackbar.showSuccess("Saved.", `Now version ${result.flow.version}.`);
            return { ok: true, flow: result.flow };
        }
        if (result.kind === "invalid") return { ok: false, problems: result.problems };
        snackbar.showError(result.message);
        return { ok: false, problems: [] };
    }, [merchantId, snackbar]);

    return { state, save, reload: load };
}

export function FlowGate({
    merchantId,
    children,
}: {
    merchantId: string | null;
    children: (props: FlowScreenProps) => ReactNode;
}) {
    const { state, save, reload } = useFlow(merchantId);
    if (state.kind === "loading") {
        return (
            <div className="flex justify-center py-24">
                <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }
    if (state.kind === "error") {
        return (
            <div>
                <Card>
                    <CardBody>
                        <EmptyState
                            icon={AlertTriangleIcon}
                            title="Couldn't load the conversation"
                            description="Something went wrong asking for the flow."
                            action={<Button onClick={() => void reload()}>Try again</Button>}
                        />
                    </CardBody>
                </Card>
            </div>
        );
    }
    return <>{children({ flow: state.flow, onSave: save })}</>;
}
